import { useState, useCallback, useEffect } from "react";
import { Textarea } from "@/components/ui/textarea";
import { FileUpload } from "@/components/ui/file-upload";
import { VoucherCodeFileUpload } from "../VoucherCodeFileUpload/VoucherCodeFileUpload";
import type { VoucherCodesValidationResult, VoucherCategoryDropdownItem } from "../../../../domain/model/RewardCatalogModel";
import { FetchVoucherCategoriesDropdownUseCase } from "../../../../domain/usecase";
import { fileToBase64, FileValidationError } from "@/utils/fileUtils";
import { Select } from "@/components/ui/select";

export interface VoucherFieldsSectionProps {
  readonly category: string;
  readonly categoryError?: string;
  readonly categoryDisabled?: boolean;
  readonly onChangeCategory: (value: string) => void;
  readonly description: string;
  readonly descriptionError?: string;
  readonly termsAndConditions: string;
  readonly termsAndConditionsError?: string;
  readonly bannerImage: string;
  readonly bannerImageError?: string;
  readonly voucherCode: string;
  readonly voucherCodeError?: string;
  readonly onChangeDescription: (value: string) => void;
  readonly onChangeTermsAndConditions: (value: string) => void;
  readonly onChangeBannerImage: (value: string) => void;
  readonly onChangeVoucherCode: (value: string, file?: File | null) => void;
  readonly onVoucherCodeValidationComplete?: (
    result: VoucherCodesValidationResult | null
  ) => void;
}

export const VoucherFieldsSection = ({
  category,
  categoryError,
  categoryDisabled = false,
  onChangeCategory,
  description,
  descriptionError,
  termsAndConditions,
  termsAndConditionsError,
  bannerImage,
  bannerImageError,
  voucherCode,
  voucherCodeError,
  onChangeDescription,
  onChangeTermsAndConditions,
  onChangeBannerImage,
  onChangeVoucherCode,
  onVoucherCodeValidationComplete,
}: VoucherFieldsSectionProps): React.JSX.Element => {
  const [isConverting, setIsConverting] = useState(false);
  const [conversionError, setConversionError] = useState<string | null>(null);
  const [categories, setCategories] = useState<readonly VoucherCategoryDropdownItem[]>([]);
  const [isCategoryLoading, setIsCategoryLoading] = useState(true);
  const [categoryFetchError, setCategoryFetchError] = useState<string | null>(null);

  // Fetch voucher categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsCategoryLoading(true);
        setCategoryFetchError(null);
        const data = await FetchVoucherCategoriesDropdownUseCase();
        setCategories(data);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
        setCategoryFetchError("Failed to load categories");
      } finally {
        setIsCategoryLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const handleBannerImageChange = useCallback(
    async (_fileName: string, file?: File | null) => {
      setConversionError(null);

      // If no file, clear the banner image
      if (!file) {
        onChangeBannerImage("");
        return;
      }

      setIsConverting(true);
      try {
        const result = await fileToBase64(file);
        onChangeBannerImage(result.base64);
      } catch (error) {
        if (error instanceof FileValidationError) {
          setConversionError(error.message);
        } else {
          setConversionError("Failed to process image file");
        }
        onChangeBannerImage("");
      } finally {
        setIsConverting(false);
      }
    },
    [onChangeBannerImage]
  );

  const displayBannerError = bannerImageError ?? conversionError ?? undefined;
  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="category"
            className="text-sm font-semibold text-gray-800 text-left"
          >
            Category
          </label>
          <Select
            id="category"
            value={category}
            onChange={(event) => onChangeCategory(event.target.value)}
            errorMessage={categoryError ?? categoryFetchError ?? undefined}
            disabled={isCategoryLoading || categoryDisabled}
          >
            <option value="" disabled>
              {isCategoryLoading ? "Loading..." : "Select Category"}
            </option>
            {categories.map((cat) => (
              <option key={cat.categoryId} value={cat.categoryId}>
                {cat.name}
              </option>
            ))}
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="description"
            className="text-sm font-semibold text-gray-800 text-left"
          >
            Description
          </label>
          <Textarea
            id="description"
            value={description}
            onChange={(event) => onChangeDescription(event.target.value)}
            placeholder="Description"
            rows={4}
            className={descriptionError ? "border-red-600" : ""}
          />
          {descriptionError && (
            <span className="text-xs text-red-600">{descriptionError}</span>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="termsAndConditions"
            className="text-sm font-semibold text-gray-800 text-left"
          >
            Terms &amp; Conditions
          </label>
          <Textarea
            id="termsAndConditions"
            value={termsAndConditions}
            onChange={(event) => onChangeTermsAndConditions(event.target.value)}
            placeholder="Terms &amp; Conditions"
            rows={4}
            className={termsAndConditionsError ? "border-red-600" : ""}
          />
          {termsAndConditionsError && (
            <span className="text-xs text-red-600">
              {termsAndConditionsError}
            </span>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="bannerImage"
            className="text-sm font-semibold text-gray-800 text-left"
          >
            Banner Image
          </label>
          <FileUpload
            id="bannerImage"
            value={bannerImage}
            onChange={handleBannerImageChange}
            accept="image/*"
            acceptText="File type: .jpeg or .png"
            maxSizeText="Maximum size: 5MB"
            variant="image"
            errorMessage={displayBannerError}
          />
          {isConverting && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
              <span>Processing image...</span>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="voucherCode"
            className="text-sm font-semibold text-gray-800 text-left"
          >
            Voucher Code
          </label>
          <VoucherCodeFileUpload
            id="voucherCode"
            value={voucherCode}
            onChange={(fileName, file) => onChangeVoucherCode(fileName, file)}
            onValidationComplete={onVoucherCodeValidationComplete}
            errorMessage={voucherCodeError}
          />
        </div>
      </div>
    </>
  );
};
